import React from 'react';
import { providers } from '../../mock';
import { Card, CardContent } from '../ui/card';

const ProviderCard = ({ provider, defaultHeight, defaultPosition }) => (
  <Card className="provider-card" data-testid={`provider-card-${provider.id}`}>
    <div className="aspect-w-16 aspect-h-12 bg-gray-200 overflow-hidden">
      <img
        src={provider.image}
        alt={provider.name}
        className={`w-full object-cover ${provider.customHeight || defaultHeight}`}
        style={{ objectPosition: provider.imagePosition || defaultPosition }}
      />
    </div>
    <CardContent className="p-6">
      <h3 className="text-xl font-bold mb-2" style={{ color: 'var(--sage-dark)' }}>{provider.name}</h3>
      <p className="font-semibold mb-2" style={{ color: 'var(--earth-accent)' }}>{provider.title}</p>
      {provider.credentials && <p className="text-gray-600 text-sm">{provider.credentials}</p>}
    </CardContent>
  </Card>
);

export const TeamSection = () => {
  return (
    <section id="providers" className="section section-alt" data-testid="team-section">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold mb-4" style={{ color: 'var(--sage-dark)' }}>Our Medical Team</h2>
          <p className="text-lg text-gray-700 max-w-2xl mx-auto">
            Meet our highly qualified team of board-certified physicians and certified nurse
            practitioners dedicated to your health and wellness.
          </p>
        </div>

        {/* CEO and Medical Director - Featured */}
        <div className="mb-16">
          <div className="max-w-5xl mx-auto">
            {providers.filter(p => p.type === 'ceo').map((provider) => (
              <Card key={provider.id} className="provider-card" data-testid={`provider-card-${provider.id}`}>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="md:col-span-1">
                    <div className="aspect-w-3 aspect-h-4 bg-gray-200 overflow-hidden rounded-l-lg">
                      <img
                        src={provider.image}
                        alt={provider.name}
                        className="w-full h-96 object-cover"
                        style={{ objectPosition: provider.imagePosition || 'center 30%' }}
                      />
                    </div>
                  </div>
                  <div className="md:col-span-2">
                    <CardContent className="p-8">
                      <h3 className="text-3xl font-bold mb-2" style={{ color: 'var(--sage-dark)' }}>{provider.name}</h3>
                      <p className="text-xl font-semibold mb-3" style={{ color: 'var(--earth-accent)' }}>{provider.title}</p>
                      <p className="text-gray-700 font-medium mb-4">{provider.credentials}</p>
                      <div className="border-t-2 pt-4 mt-4" style={{ borderColor: 'var(--earth-accent)' }}>
                        <p className="text-gray-700 leading-relaxed">{provider.bio}</p>
                      </div>
                    </CardContent>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>

        {/* Primary Physicians */}
        <div className="mb-12">
          <h3 className="text-2xl font-bold mb-6 text-center" style={{ color: 'var(--sage-dark)' }}>Primary Physicians</h3>
          <div className="flex justify-center max-w-4xl mx-auto">
            {providers.filter(p => p.type === 'physician').map((provider) => (
              <div key={provider.id} className="w-full md:w-96">
                <ProviderCard provider={provider} defaultHeight="h-80" defaultPosition="center 20%" />
              </div>
            ))}
          </div>
        </div>

        {/* Collaborating Physicians */}
        <div className="mb-12">
          <h3 className="text-2xl font-bold mb-6 text-center" style={{ color: 'var(--sage-dark)' }}>Collaborating Physicians</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {providers.filter(p => p.type === 'collaborating').map((provider) => (
              <ProviderCard key={provider.id} provider={provider} defaultHeight="h-72" defaultPosition="center 25%" />
            ))}
          </div>
        </div>

        {/* Advanced Practice Providers */}
        <div className="mb-12">
          <h3 className="text-2xl font-bold mb-6 text-center" style={{ color: 'var(--sage-dark)' }}>Advanced Practice Providers</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {providers.filter(p => p.type === 'nurse_practitioner').map((provider) => (
              <ProviderCard key={provider.id} provider={provider} defaultHeight="h-72" defaultPosition="center 25%" />
            ))}
          </div>
        </div>

        {/* Care Team */}
        <div>
          <h3 className="text-2xl font-bold mb-6 text-center" style={{ color: 'var(--sage-dark)' }}>Our Care Team</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {providers.filter(p => p.type === 'care_manager' || p.type === 'office_manager').map((provider) => (
              <ProviderCard key={provider.id} provider={provider} defaultHeight="h-80" defaultPosition="center 20%" />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
